/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, memo, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { getIn } from "formik";

import { ErrorMessage, ContexualHelp } from "./../../../Notifications";
import { Control, Option, DropdownIndicator, MultiValue } from "./SelectAutocompleteComponents";
import { areEqualFieldProps } from "../../utils";
import { customStyles, StyledFormControl, StyledSelectInput } from "./styled";
import { InfoTitle } from "../../../InfoTitle";

const SelectAutocompleteBase = ({
  extractValue = option => option?.value || (option?.value === false ? false : ""),
  extractLabel = option => option?.label || option?.displayText,
  theme,
  label,
  shrink,
  options,
  infoTitle,
  field: { onBlur, ...field },
  form: { errors, touched, setFieldValue, setFieldTouched },
  multiple = false,
  disabled,
  contextualHelpText,
  contextualHelpProps,
  onChange = value => setFieldValue(field.name, value),
  customCheckbox = false,
  infoIcon = false,
  isClearable = false,
  classes: customClasses,
  selectOptionPlacement = "auto",
  ...props
}) => {
  const selectRef = useRef();
  const errorMessage = getIn(errors, field.name);
  const isError = errorMessage && getIn(touched, field.name);
  const [hasFocus, setHasFocus] = useState(false);

  const renderValue = !multiple
    ? options?.find(option => extractValue(option) === field.value)
    : options.filter(option => (field.value || []).map(extractValue).includes(option.value));

  /*istanbul ignore next*/
  const handleChange = selected => {
    const value = multiple ? (selected || []).map(item => item.value) : extractValue(selected);
    const selectInnerRef = props.innerRef || selectRef;
    if (!multiple && selectInnerRef && selectInnerRef.current) {
      setTimeout(() => {
        selectInnerRef.current && selectInnerRef.current.blur();
      }, 0);
    }
    let renderValue = options?.find(option => extractValue(option) === value);
    return onChange(value, renderValue);
  };

  const components = useMemo(
    () => ({
      Control,
      Option,
      MultiValue,
      [isClearable ? "DropdownIndicator" : "IndicatorsContainer"]: DropdownIndicator
    }),
    []
  );

  useEffect(() => {
    if (disabled) {
      if (props.innerRef && props.innerRef.current) {
        props.innerRef.current.onMenuClose();
      }
    }
  }, [disabled]);

  return (
    <StyledFormControl
      variant="filled"
      data-testid={"select-autocomplete"}
      className={customClasses}
      aria-label={`${field.name}-select`}
    >
      <ContexualHelp title={contextualHelpText} {...contextualHelpProps}>
        <label htmlFor={field.name} hidden>
          {typeof field.value === "string" ? field.value : ""}
        </label>
        <StyledSelectInput
          {...field}
          {...props}
          ref={props.innerRef || selectRef}
          isOpen
          options={options}
          styles={customStyles}
          components={components}
          value={renderValue || ""}
          onChange={handleChange}
          isMulti={multiple}
          customCheckbox={customCheckbox}
          isDisabled={disabled}
          closeMenuOnSelect={!multiple}
          tabSelectsValue={!multiple}
          hideSelectedOptions={false}
          joinValues={true}
          delimiter=","
          placeholder={""}
          menuPlacement={selectOptionPlacement}
          minMenuHeight={300}
          labelText={label}
          textFieldProps={{
            onFocus: () => setHasFocus(true),
            onBlur: () => {
              setHasFocus(false);
              setFieldTouched(field.name);
            },
            label,
            name: field.name,
            error: !!isError,
            InputLabelProps: {
              shrink: hasFocus || !!(Array.isArray(renderValue) ? renderValue.length : renderValue)
            }
          }}
          getOptionLabel={extractLabel}
          isClearable={isClearable}
        />
      </ContexualHelp>
      {infoTitle && (
        <InfoTitle title={infoTitle} showIcon={infoIcon} className="selectFieldDescription" />
      )}
      {isError && <ErrorMessage error={errorMessage} />}
    </StyledFormControl>
  );
};

SelectAutocompleteBase.propTypes = {
  extractValue: PropTypes.func,
  extractLabel: PropTypes.func,
  theme: PropTypes.object,
  label: PropTypes.string,
  shrink: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string,
      displayText: PropTypes.string
    })
  ).isRequired,
  infoTitle: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
    onBlur: PropTypes.func
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired
  }).isRequired,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  contextualHelpText: PropTypes.string,
  contextualHelpProps: PropTypes.object,
  onChange: PropTypes.func,
  customCheckbox: PropTypes.bool,
  infoIcon: PropTypes.bool,
  isClearable: PropTypes.bool,
  classes: PropTypes.string,
  selectOptionPlacement: PropTypes.string,
  innerRef: PropTypes.object
};

export const SelectAutocomplete = memo(SelectAutocompleteBase, areEqualFieldProps);
